import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Loader2 } from "lucide-react";
import { GROUPS, STRIPE_COURSE_LINK, STRIPE_TRIAL_LINK } from "../lib/content";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingDialog({ open, onOpenChange, packageId = "trial_class", defaultGroup }) {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    preferred_group: defaultGroup || "fri-1900-alina",
    comment: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const isBookable =
        defaultGroup && !GROUPS.find((g) => g.value === defaultGroup)?.soldOut;
      setForm((f) => ({ ...f, preferred_group: isBookable ? defaultGroup : "fri-1900-alina" }));
    }
  }, [open, defaultGroup]);

  const isCourse = packageId === "hipno_bedra";
  const stripeLink = isCourse ? STRIPE_COURSE_LINK : STRIPE_TRIAL_LINK;
  const title = isCourse ? "Купити онлайн-урок" : "Бронювання пробного заняття";
  const price = isCourse ? "49 zł" : "50 zł";
  const subtitle = isCourse
    ? "Залиш контакти — і одразу переходимо до оплати."
    : "Місце фіксується після оплати. Бронювання не переноситься і не заморожується.";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) {
      toast.error("Заповни ім'я та контакт");
      return;
    }
    setLoading(true);
    try {
      const groupLabel =
        GROUPS.find((g) => g.value === form.preferred_group)?.label || form.preferred_group;
      const { data } = await axios.post(`${API}/bookings/lead`, {
        name: form.name.trim(),
        contact: form.contact.trim(),
        preferred_group: isCourse ? null : groupLabel,
        comment: form.comment.trim(),
        package_id: packageId,
        origin_url: window.location.origin,
      });
      const ref = data?.booking_id ? `?client_reference_id=${data.booking_id}` : "";
      window.location.href = `${stripeLink}${ref}`;
    } catch (err) {
      console.error(err);
      toast.error("Помилка. Спробуй ще раз або напиши нам в Instagram.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#0F0F12] border border-white/10 text-[#FDFBF7] max-w-md sm:max-w-lg p-0 overflow-hidden"
      >
        <div className="p-6 sm:p-8">
          <DialogHeader className="text-left space-y-2 mb-6">
            <p className="overline">Grand Dance Studio</p>
            <DialogTitle className="font-serif-display text-3xl sm:text-4xl font-light leading-tight">
              {title}
            </DialogTitle>
            <DialogDescription className="text-[#A19E98] text-sm">
              {subtitle}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-[#A19E98]">
                Ім'я
              </Label>
              <Input
                id="name"
                data-testid="booking-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Анна"
                className="mt-2 bg-[#141417] border-white/10 text-[#FDFBF7] placeholder:text-white/30 focus:border-[#7A1220] focus-visible:ring-0 rounded-none h-12"
              />
            </div>

            <div>
              <Label htmlFor="contact" className="text-xs uppercase tracking-widest text-[#A19E98]">
                Телефон · Telegram · Instagram
              </Label>
              <Input
                id="contact"
                data-testid="booking-contact-input"
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                placeholder="+48 ... або @username"
                className="mt-2 bg-[#141417] border-white/10 text-[#FDFBF7] placeholder:text-white/30 focus:border-[#7A1220] focus-visible:ring-0 rounded-none h-12"
              />
            </div>

            {!isCourse && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#A19E98]">
                  Обрана група
                </Label>
                <Select
                  value={form.preferred_group}
                  onValueChange={(v) => setForm({ ...form, preferred_group: v })}
                >
                  <SelectTrigger
                    data-testid="booking-group-select"
                    className="mt-2 bg-[#141417] border-white/10 text-[#FDFBF7] focus:border-[#7A1220] focus:ring-0 rounded-none h-12"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141417] border-white/10 text-[#FDFBF7]">
                    {GROUPS.filter((g) => g.level !== "STARS" && !g.soldOut).map((g) => (
                      <SelectItem
                        key={g.value}
                        value={g.value}
                        data-testid={`booking-group-option-${g.value}`}
                        className="focus:bg-[#7A1220]/30 focus:text-[#FDFBF7]"
                      >
                        {g.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="comment" className="text-xs uppercase tracking-widest text-[#A19E98]">
                Коментар <span className="text-white/30 normal-case tracking-normal">(не обов'язково)</span>
              </Label>
              <Textarea
                id="comment"
                data-testid="booking-comment-input"
                rows={2}
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder="Що тебе турбує або що хочеш від занять?"
                className="mt-2 bg-[#141417] border-white/10 text-[#FDFBF7] placeholder:text-white/30 focus:border-[#7A1220] focus-visible:ring-0 rounded-none resize-none"
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-white/5">
              <div className="text-[#A19E98] text-sm">До оплати</div>
              <div className="font-serif-display text-3xl text-[#FDFBF7]">{price}</div>
            </div>

            <button
              type="submit"
              data-testid="booking-submit-button"
              disabled={loading}
              className="w-full h-14 bg-[#7A1220] hover:bg-[#A01828] active:scale-[0.99] transition-all duration-300 text-[#FDFBF7] font-medium tracking-wider uppercase text-xs disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Переходимо до оплати...
                </>
              ) : (
                <>Перейти до оплати · {price}</>
              )}
            </button>

            <p className="text-[11px] text-white/40 leading-relaxed text-center pt-1">
              Оплата проходить безпечно через Stripe. Після оплати ми побачимо твою заявку
              у системі студії.
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
